/**
 * AdminMarketFeedsCard — Super Admin › Market Feeds
 *
 * Shows the configured providers (FMP + Mansa), lets the admin quick-test
 * any symbol, and surfaces routing (mansa vs fmp) so we can prove East
 * African symbols route to the regional feed.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Zap, Globe, MapPin, Activity, AlertTriangle } from "lucide-react";
import { Jargon } from "@/components/admin/JargonTerm";

export default function AdminMarketFeedsCard() {
    const [status, setStatus] = useState(null);
    const [telemetry, setTelemetry] = useState(null);
    const [symbol, setSymbol] = useState("AAPL");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    async function loadStatus() {
        try {
            const [s, t] = await Promise.all([
                api.get("/admin/market-feeds/status"),
                api.get("/admin/telemetry/feeds"),
            ]);
            setStatus(s.data);
            setTelemetry(t.data);
        } catch (e) {
            toast.error("Failed to load provider status");
        }
    }

    useEffect(() => {
        loadStatus();
        // Heartbeat every 60s — cheap and matches the FMP quote-cache TTL.
        const id = setInterval(loadStatus, 60_000);
        return () => clearInterval(id);
    }, []);

    async function runTest(e) {
        e.preventDefault();
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post(`/admin/market-feeds/test?symbol=${encodeURIComponent(symbol)}`);
            setResult(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Test failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-4" data-testid="admin-market-feeds-card">
            {telemetry?.mansa?.quota_exhausted && (
                <div className="border-2 border-rose-500/60 bg-rose-500/10 rounded-xl p-4 flex items-start gap-3" data-testid="mansa-quota-exhausted-banner">
                    <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-semibold text-rose-500">Mansa daily <Jargon term="quota" /> exhausted</div>
                        <div className="text-muted-foreground mt-0.5">
                            {telemetry.mansa.quota_message}
                            {telemetry.mansa.quota_reset_at && (
                                <> Resets at <span className="font-semibold text-foreground">{new Date(telemetry.mansa.quota_reset_at).toLocaleString()}</span>.</>
                            )}
                            {" "}All further Mansa calls are paused automatically until then — no budget is wasted retrying.
                        </div>
                    </div>
                </div>
            )}
            {/* Health heartbeat strip */}
            {telemetry && (
                <div className="border border-border bg-card rounded-xl p-4" data-testid="admin-market-feeds-telemetry">
                    <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-4 h-4 text-primary" />
                        <div className="overline">Live-feed <Jargon term="heartbeat" /></div>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                            Last check {new Date(telemetry.checked_at).toLocaleTimeString()}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {["mansa"].map((k) => {
                            const t = telemetry[k] || {};
                            const dot = t.status === "ok" ? "bg-emerald-500" : t.status === "degraded" ? "bg-amber-500" : "bg-rose-500";
                            const b = t.budget || {};
                            const pct = b.ceiling ? Math.min(100, Math.round(100 * (b.used_today || 0) / b.ceiling)) : 0;
                            return (
                                <div key={k} className="rounded border border-border p-3" data-testid={`telemetry-${k}`}>
                                    <div className="flex items-center gap-2 text-sm font-semibold uppercase">
                                        <span className={`w-2 h-2 rounded-full ${dot}`}></span>
                                        {k}
                                        <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
                                            <Jargon term="latency" />{t.latency_ms != null ? `: ${t.latency_ms} ms` : ""}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                        {t.status?.toUpperCase() || "—"}{t.http_code ? ` · HTTP ${t.http_code}` : ""}
                                        {t.last_error && (<span className="block text-rose-500 truncate">{t.last_error}</span>)}
                                    </div>
                                    <div className="mt-2">
                                        <div className="text-[10px] uppercase text-muted-foreground">
                                            Budget · {b.used_today || 0} / {b.ceiling || "—"} calls today
                                        </div>
                                        <div className="h-1 bg-muted rounded overflow-hidden mt-1">
                                            <div
                                                className={"h-full " + (pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500")}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {["mansa"].map((k) => {
                    const s = status?.providers?.[k] || status?.[k] || {};
                    return (
                        <div key={k} className="border border-border bg-card rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin className="w-4 h-4 text-primary" />
                                <div className="overline">
                                    Mansa · Pan-African Data Feed
                                </div>
                            </div>
                            <div className="text-sm space-y-1" data-testid={`market-feeds-status-${k}`}>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Configured</span>
                                    <span className={s.configured ? "text-emerald-500 font-semibold" : "text-amber-500 font-semibold"}>
                                        {s.configured ? "yes" : "no"}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Base URL</span>
                                    <span className="mono text-xs truncate max-w-[60%]" title={s.base_url}>{s.base_url || "—"}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {status?.mansa_universe && (
                <div className="border border-border bg-card rounded-xl p-4">
                    <div className="overline mb-2">Mansa routing universe</div>
                    <p className="text-xs text-muted-foreground mb-2">
                        Any symbol in this list is routed to Mansa. All market data
                        is served exclusively from the Mansa Markets API.
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {status.mansa_universe.map((s) => (
                            <span key={s} className="mono text-xs px-2 py-0.5 rounded bg-secondary">{s}</span>
                        ))}
                    </div>
                </div>
            )}

            <form onSubmit={runTest} className="border border-border bg-card rounded-xl p-4 space-y-3">
                <div className="overline">Quick test</div>
                <div className="flex gap-2 items-end">
                    <label className="block flex-1">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Symbol</span>
                        <Input
                            value={symbol}
                            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                            data-testid="market-feeds-test-symbol"
                        />
                    </label>
                    <Button type="submit" disabled={busy || !symbol} data-testid="market-feeds-test-run">
                        <Zap className="w-4 h-4 mr-1" /> {busy ? "Testing…" : "Test"}
                    </Button>
                </div>
                {result && (
                    <pre
                        className="text-xs mono bg-muted/40 rounded p-3 overflow-x-auto"
                        data-testid="market-feeds-test-result"
                    >{JSON.stringify(result, null, 2)}</pre>
                )}
            </form>
        </div>
    );
}
